import { motion } from 'framer-motion';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const Privacy = () => {
  return (
    <div data-testid="privacy-page">
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-primary mb-6">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground mb-8">
              Last updated: 14 March 2026
            </p>

            <div className="prose prose-slate max-w-none space-y-8">
              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Who We Are</h2>
                <p className="text-muted-foreground">
                  Dockside Design is a trading name of Altorite LTD. We are a web design studio based in the United Kingdom.
                  You can contact us at{' '}
                  <a href="mailto:support@docksidedesign.co.uk" className="text-accent hover:underline">
                    support@docksidedesign.co.uk
                  </a>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">What Data We Collect</h2>
                <p className="text-muted-foreground mb-3">We collect data in the following ways:</p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    <strong className="text-foreground">Contact form submissions:</strong> Your name, email address, phone number (optional),
                    business name (optional), and message content.
                  </li>
                  <li>
                    <strong className="text-foreground">Email subscriptions:</strong> Your email address when you sign up for updates.
                  </li>
                  <li>
                    <strong className="text-foreground">Booking requests:</strong> Your name, email address, date, time, party size, and any notes.
                  </li>
                  <li>
                    <strong className="text-foreground">Analytics cookies:</strong> If you accept cookies, Google Analytics 4 collects
                    anonymised usage data (pages visited, session duration, device type). No personally identifiable information
                    is collected through analytics.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Why We Collect It (Lawful Basis)</h2>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    <strong className="text-foreground">Contact forms &amp; bookings:</strong> Legitimate interest — to respond to your enquiry
                    and provide the service you requested (UK GDPR Article 6(1)(f)).
                  </li>
                  <li>
                    <strong className="text-foreground">Email subscriptions:</strong> Consent — you actively opt in by subscribing and confirming
                    via a double opt-in email (UK GDPR Article 6(1)(a)).
                  </li>
                  <li>
                    <strong className="text-foreground">Analytics cookies:</strong> Consent — cookies are only set after you click "Accept" on
                    the cookie banner (PECR Regulation 6).
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">How Long We Keep Your Data</h2>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    <strong className="text-foreground">Contact form submissions:</strong> Retained for up to 12 months, then automatically deleted.
                  </li>
                  <li>
                    <strong className="text-foreground">Booking requests:</strong> Retained for up to 12 months after the booking date, then automatically deleted.
                  </li>
                  <li>
                    <strong className="text-foreground">Email subscriptions:</strong> Retained until you unsubscribe or request deletion.
                  </li>
                  <li>
                    <strong className="text-foreground">Analytics data:</strong> Google Analytics retains data for 14 months by default.
                    We do not export or store analytics data separately.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Who We Share Your Data With</h2>
                <p className="text-muted-foreground mb-3">
                  We do not sell your data. We share data only with the following service providers who process it on our behalf:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    <strong className="text-foreground">Amazon Web Services (AWS):</strong> Hosts our website and stores form submissions.
                    Data is processed in the EU (Stockholm, eu-north-1 region).
                  </li>
                  <li>
                    <strong className="text-foreground">Google Analytics:</strong> Processes anonymised analytics data (only if you accept cookies).
                    Google may transfer data outside the UK under their standard contractual clauses.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Cookies</h2>
                <p className="text-muted-foreground mb-3">
                  We use one type of cookie on this site:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>
                    <strong className="text-foreground">Google Analytics cookies (_ga, _ga_*):</strong> These are only set if you click
                    "Accept" on the cookie banner. They help us understand how visitors use the site.
                  </li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  We also store your cookie preference in your browser's local storage (not a cookie) so we don't ask you again
                  on each visit. You can clear this at any time via your browser settings.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Your Rights</h2>
                <p className="text-muted-foreground mb-3">
                  Under UK GDPR, you have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li><strong className="text-foreground">Access</strong> the personal data we hold about you</li>
                  <li><strong className="text-foreground">Rectify</strong> any inaccurate data</li>
                  <li><strong className="text-foreground">Erase</strong> your data ("right to be forgotten")</li>
                  <li><strong className="text-foreground">Withdraw consent</strong> for email subscriptions or analytics cookies at any time</li>
                  <li><strong className="text-foreground">Object</strong> to processing based on legitimate interest</li>
                  <li><strong className="text-foreground">Lodge a complaint</strong> with the Information Commissioner's Office (ICO) at{' '}
                    <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                      ico.org.uk
                    </a>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  To exercise any of these rights, email us at{' '}
                  <a href="mailto:support@docksidedesign.co.uk" className="text-accent hover:underline">
                    support@docksidedesign.co.uk
                  </a>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Changes to This Policy</h2>
                <p className="text-muted-foreground">
                  We may update this policy from time to time. Any changes will be posted on this page with an updated date.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
